import { ColumnsType } from 'antd/lib/table'
import { useCallback } from 'react'
import {
  CIMSColumnFilterItem,
  FilterOptions,
} from '../../../CIMSColumn/types/filter'
import { useGetFilterIcon } from './hooks/useGetFilterIcon'
import { useGetOnFilter } from './hooks/useGetOnFilter'
import { useColumnFilterItems } from './hooks/useColumnFilterItems'
import ColumnSearch from './components/ColumnSearch'
import ColumnDatePicker from './components/ColumnDatePicker'
import ColumnNumberSearch from './components/ColumnNumberSearch'
import { useBooleanColumnFilterItems } from './hooks/useBooleanColumnFilterItems'

export function useSetFilterOptions<T extends object>(
  records: T[] | undefined
) {
  const getFilterIcon = useGetFilterIcon<T>()
  const getOnFilter = useGetOnFilter<T>()
  const columnFilterItems = useColumnFilterItems()
  const booleanColumnFilterItems = useBooleanColumnFilterItems()
  return useCallback(
    (column: ColumnsType<T>[number], filter: FilterOptions<T>) => {
      /**
       * set onFilter callback and filter icon for all filter types
       */
      column.onFilter = getOnFilter(filter)
      column.filterIcon = getFilterIcon(filter)

      /**
       * set additional column options per filter type
       */
      if (filter.type === 'boolean') {
        column.filters = booleanColumnFilterItems(filter.labels)
        if (filter.default !== undefined) {
          column.defaultFilteredValue = [String(filter.default)]
        }
      } else if (filter.type === 'checkboxes') {
        const checkboxes =
          typeof filter.items === 'function'
            ? filter.items(records ?? [])
            : filter.items
        column.filters = columnFilterItems(
          checkboxes?.map<CIMSColumnFilterItem>(({ text, value, tooltip }) => {
            const labels =
              typeof filter.labels === 'function'
                ? filter.labels(value)
                : filter.labels
            const children = booleanColumnFilterItems(labels, (checked) =>
              JSON.stringify({ value, checked })
            )
            return { text, value, tooltip, children }
          })
        )
      } else if (filter.type === 'date') {
        column.filterDropdown = (props) => <ColumnDatePicker {...props} />
      } else if (filter.type === 'number') {
        column.filterDropdown = (props) => <ColumnNumberSearch {...props} />
      } else if (filter.type === 'search') {
        column.filterDropdown = (props) => <ColumnSearch {...props} />
      } else {
        let items
        if (filter.with === 'key')
          items = (records ?? [])
            .map((record) => record[filter.key] as string | number)
            .sort()
        else if (typeof filter.items === 'function')
          items = filter.items(records ?? [])
        else items = filter.items ?? []
        column.filters = columnFilterItems(items)
      }
    },
    [
      booleanColumnFilterItems,
      columnFilterItems,
      getFilterIcon,
      getOnFilter,
      records,
    ]
  )
}
